import dayjs, { ConfigType } from "dayjs"
import zhTW from "dayjs/locale/zh-tw"
import customParseFormat from "dayjs/plugin/customParseFormat"
import duration from "dayjs/plugin/duration"
import localeData from "dayjs/plugin/localeData"
import minMax from "dayjs/plugin/minMax"
import relativeTime from "dayjs/plugin/relativeTime"
import updateLocale from "dayjs/plugin/updateLocale"
import utc from "dayjs/plugin/utc"
import weekday from "dayjs/plugin/weekday"

// 自定義中文相對時間顯示
const customLocale = {
  ...zhTW,
  relativeTime: {
    future: "%s後",
    past: "%s前",
    s: "幾秒",
    m: "1分鐘",
    mm: "%d分鐘",
    h: "1小時",
    hh: "%d小時",
    d: "1天",
    dd: "%d天",
    M: "1個月",
    MM: "%d個月",
    y: "1年",
    yy: "%d年",
  },
}

dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime, {
  thresholds: [
    { l: "s", r: 1 },
    { l: "m", r: 1 },
    { l: "mm", r: 59, d: "分鐘" },
    { l: "h", r: 1 },
    { l: "hh", r: 23, d: "小時" },
    { l: "d", r: 1 },
    { l: "dd", r: 29, d: "天" },
    { l: "M", r: 1 },
    { l: "MM", r: 11, d: "個月" },
    { l: "y" },
    { l: "yy", d: "年" },
  ],
})
dayjs.extend(updateLocale)
dayjs.extend(minMax)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.locale("zh-tw", customLocale)
const day = (config?: ConfigType, format?: string, strict?: boolean) => {
  return dayjs(config, format, strict).utcOffset(-4, false)
}

day.utc = dayjs.utc
day.duration = dayjs.duration
day.min = dayjs.min
day.max = dayjs.max

function getTzOffsetMs() {
  const targetTz = -4 // UTC-4
  return (new Date().getTimezoneOffset() + targetTz * 60) * 60 * 1000
}

day.toUTCDate = (date: Date = new Date()) => {
  return new Date(date.getTime() + getTzOffsetMs())
}

day.toLocalDate = (date: Date = new Date()) => {
  return new Date(date.getTime() - getTzOffsetMs())
}

day.formatLastActive = (date: string | Date) => {
  if (!date) return ""

  const dateObj = day(date)
  const now = day()
  const diffMinutes = Math.abs(now.diff(dateObj, "minutes"))

  // 直接處理相對時間的顯示
  if (diffMinutes < 60) {
    const mins = Math.floor(diffMinutes)
    return mins === 0 ? "現在" : `${mins}分鐘前`
  }

  if (diffMinutes < 1440) {
    const hours = Math.floor(diffMinutes / 60)
    return `${hours}小時前`
  }

  const diffDays = Math.floor(diffMinutes / 1440)
  if (diffDays < 30) {
    return `${diffDays}天前`
  }

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) {
    return `${diffMonths}個月前`
  }

  // 超過一年就顯示日期
  const currentYear = now.year()
  const dateYear = dateObj.year()

  if (dateYear < currentYear - 1) {
    return dateObj.format("YYYY/MM/DD HH:mm")
  }

  return dateObj.format("MM/DD HH:mm")
}

export default day
